/**
 * Helper functions for K8ProgramStatusRouter
 *
 * This file contains utility functions for determining user status
 * based on their preferences, training, and classes.
 */

import { programs } from 'layers/content/v2/hooks/useCourseManager'
import { YearsTeachingExperience } from 'utils/product/helpers'
import {
  isK5Dashboard,
  isMiddleSchoolDashboard,
} from 'utils/routing/routeHelpers'

/**
 * Determines if a user has set their years of teaching preference
 *
 * @param {Object} programPreferences - The program preferences object
 * @returns {boolean} - True if yearsTeaching is set, false otherwise
 */
export const hasYearsTeaching = (programPreferences = {}) => {
  return (
    programPreferences?.yearsTeaching !== undefined &&
    programPreferences?.yearsTeaching !== null
  )
}

/**
 * Determines if a user has set their years of teaching preference and meets the minimum experience requirement
 *
 * @param {Object} programPreferences - The program preferences object
 * @returns {Object} - Object containing hasYearsTeaching and requiredTraining boolean values
 */
export const hasYearsTeachingAndRequiredTraining = (
  programPreferences = {},
) => {
  const hasYears = hasYearsTeaching(programPreferences)

  if (!hasYears) {
    return {
      hasYearsTeaching: false,
      requiredTraining: true,
    }
  }

  const yearsTeaching = programPreferences?.yearsTeaching

  // Check if the value meets minimum experience requirement (exempts from training)
  const meetsMinimumExperience =
    yearsTeaching === YearsTeachingExperience.TWO_TO_FOUR_YEARS ||
    yearsTeaching === YearsTeachingExperience.FIVE_PLUS_YEARS

  return {
    hasYearsTeaching: true,
    requiredTraining: !meetsMinimumExperience,
  }
}

/**
 * Determines if a user has set their teacher preferences
 *
 * @param {Object} currentSite - The current site object
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {boolean} - True if all required preferences have been set, false otherwise
 */
export const hasTeacherPreferences = (
  currentSite = {},
  isK5Product = false,
  isMsProduct = false,
) => {
  // Determine which preferences to check based on product type
  let programPreferences = null

  if (isK5Product) {
    // Get K5 program preferences
    programPreferences = currentSite?.k5EducatorPreferences || {}
  } else if (isMsProduct) {
    // Get Middle School program preferences
    programPreferences = currentSite?.middleSchoolEducatorPreferences || {}
  } else {
    // If neither K5 nor MS, return false
    return false
  }

  // Check for the required fields
  const hasTeachesLessons =
    programPreferences?.teachesLessons !== undefined &&
    programPreferences?.teachesLessons !== null
  const hasProgramTrack =
    programPreferences?.programTrack !== undefined &&
    programPreferences?.programTrack !== null

  // Return true only if all required fields have values
  return (
    hasYearsTeaching(programPreferences) && hasTeachesLessons && hasProgramTrack
  )
}

/**
 * Determines if a user has created at least one class by checking instructorData
 * for registrations with the matching site_id
 *
 * @param {Object} instructorData - The instructor data from lmsManager Redux
 * @param {Object} currentSite - The current site object containing siteId
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {boolean} - True if at least one class exists for the site, false otherwise
 */
export const hasCreatedClass = (
  instructorData = {},
  currentSite = {},
  isK5Product = false,
  isMsProduct = false,
) => {
  // If no instructorData or currentSite, return false
  if (!instructorData || !currentSite?.siteId) {
    return false
  }

  // Determine which program key to use based on product type
  let programKey = null

  if (isK5Product) {
    programKey = 'K5SelDigital'
  } else if (isMsProduct) {
    programKey = 'MspDigital'
  } else {
    // If neither K5 nor MS, return false
    return false
  }

  // Check if the program exists in instructorData
  if (!instructorData?.[programKey]) {
    return false
  }

  // Check if siteEducator array exists and has at least one registration with matching site_id
  const siteEducator = instructorData?.[programKey]?.siteEducator

  if (!Array.isArray(siteEducator) || siteEducator.length === 0) {
    return false
  }

  // Check if any registration has the matching site_id
  return siteEducator.some(
    registration => registration?.site_id === currentSite?.siteId,
  )
}

/**
 * Helper function to get training course configuration and completion data
 *
 * @param {Object} courseManager - The course manager object containing course trees
 * @param {Object} instructorData - The instructor data from lmsManager Redux
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {Object|null} - Object containing trainingCourseTree, completionMap, and leafNodes, or null if invalid
 */
const getTrainingCourseData = (
  courseManager = {},
  instructorData = {},
  isK5Product = false,
  isMsProduct = false,
) => {
  // If courseManager or instructorData is missing, return null
  if (!courseManager?.courseTrees || !instructorData) {
    return null
  }

  // Determine which training course to check based on the product type
  let trainingCourseName = null
  let instructorDataKey = null

  if (isK5Product) {
    trainingCourseName = 'k-5_training_tier1'
    instructorDataKey = 'K5SelDigital'
  } else if (isMsProduct) {
    trainingCourseName = '6-8_training_tier1'
    instructorDataKey = 'MspDigital'
  } else {
    // If neither K5 nor MS, return null
    return null
  }

  // Get the training course tree
  const trainingCourseTree = courseManager?.courseTrees?.[trainingCourseName]
  if (!trainingCourseTree) {
    return null
  }

  // Get the relevant instructor data
  const relevantInstructorData = instructorData?.[instructorDataKey]
  if (!relevantInstructorData?.educator) {
    return null
  }

  // Create a map of completed course nodes from instructor data
  const completionMap = {}

  // Handle the case where educator is an array of registrations
  if (Array.isArray(relevantInstructorData?.educator)) {
    // Iterate through each registration in the educator array
    for (const registration of relevantInstructorData?.educator || []) {
      // Check each registration's lesson_nodes if it exists
      for (const lesson of registration?.lesson_nodes || []) {
        if (lesson) {
          // Extract the course node ID (remove presentation ID if present)
          const courseNodeId = lesson?.leaf_node_id?.split('-')?.[0]
          if (courseNodeId) {
            completionMap[courseNodeId] = {
              isComplete: lesson?.is_done === true,
            }
          }
        }
      }
    }
  }

  // Find all leaf nodes in the training course tree
  const leafNodes = []

  // Recursive function to traverse the course tree and collect leaf nodes
  const findLeafNodes = node => {
    if (!node) return

    // If this is a leaf node, add it to our collection
    if (node?.leaf === true) {
      leafNodes.push(node)
    }

    // If the node has children, recursively process them
    if (node?.children && node?.children?.length > 0) {
      for (const child of node?.children || []) {
        findLeafNodes(child)
      }
    }
  }

  // Start the recursive search from the root of the training course tree
  findLeafNodes(trainingCourseTree)

  // If no leaf nodes were found, return null
  if (leafNodes.length === 0) {
    return null
  }

  return {
    trainingCourseTree,
    completionMap,
    leafNodes,
  }
}

/**
 * Determines if a user has completed K8 training by checking all leaf nodes
 * in the training course tree against the instructorData
 *
 * @param {Object} courseManager - The course manager object containing course trees
 * @param {Object} instructorData - The instructor data from lmsManager Redux
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {boolean} - True if all training leaf nodes are marked as complete, false otherwise
 */
export const hasCompletedK8Training = (
  courseManager = {},
  instructorData = {},
  isK5Product = false,
  isMsProduct = false,
) => {
  const courseData = getTrainingCourseData(
    courseManager,
    instructorData,
    isK5Product,
    isMsProduct,
  )

  if (!courseData) {
    return false
  }

  const { completionMap, leafNodes } = courseData

  // Check if all leaf nodes are marked as complete in the instructor data
  for (const leafNode of leafNodes) {
    // Skip optional lessons (if any)
    if (leafNode?.requirementType === 'choice') {
      continue
    }

    // Check if this leaf node is marked as complete
    const nodeId = leafNode?.contentfulId
    if (!nodeId || !completionMap?.[nodeId]?.isComplete) {
      return false
    }
  }

  // If we've checked all required leaf nodes and they're all complete, return true
  return true
}

/**
 * Determines if a user has completed some K8 training by checking if at least one leaf node
 * in the training course tree is marked as complete in the instructorData
 *
 * @param {Object} courseManager - The course manager object containing course trees
 * @param {Object} instructorData - The instructor data from lmsManager Redux
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {boolean} - True if at least one training leaf node is marked as complete, false otherwise
 */
export const hasCompletedSomeK8Training = (
  courseManager = {},
  instructorData = {},
  isK5Product = false,
  isMsProduct = false,
) => {
  const courseData = getTrainingCourseData(
    courseManager,
    instructorData,
    isK5Product,
    isMsProduct,
  )

  if (!courseData) {
    return false
  }

  const { completionMap, leafNodes } = courseData

  // Check if at least one leaf node is marked as complete in the instructor data
  for (const leafNode of leafNodes) {
    // Skip optional lessons (if any)
    if (leafNode?.requirementType === 'choice') {
      continue
    }

    // Check if this leaf node is marked as complete
    const nodeId = leafNode?.contentfulId
    if (nodeId && completionMap?.[nodeId]?.isComplete) {
      return true // Found at least one completed training module
    }
  }

  // If no completed training modules were found, return false
  return false
}

/**
 * Determines which preferences to check based on product type and extracts requiredTraining
 *
 * @param {Object} currentSite - The current site object
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {Object} - Object containing programPreferences and requiredTraining values
 */
export const getProgramPreferencesAndTraining = (
  currentSite = {},
  isK5Product = false,
  isMsProduct = false,
) => {
  let requiredTraining = null
  let programPreferences = null

  if (isK5Product) {
    programPreferences = currentSite?.k5EducatorPreferences || {}
    const yearsTeachingData = hasYearsTeachingAndRequiredTraining(
      programPreferences,
    )
    requiredTraining = yearsTeachingData?.requiredTraining
  } else if (isMsProduct) {
    programPreferences = currentSite?.middleSchoolEducatorPreferences || {}
    const yearsTeachingData = hasYearsTeachingAndRequiredTraining(
      programPreferences,
    )
    requiredTraining = yearsTeachingData?.requiredTraining
  }

  return {
    programPreferences,
    requiredTraining,
  }
}

/**
 * Custom program resolver for CourseManagerGetter
 *
 * Determines which program to use based on the current dashboard page
 *
 * @returns {string} The program to use (middleSchoolWithTraining, elementaryWithTraining, or none)
 */
export const programResolver = (_, props) => {
  if (!props?.userContext?.sites) {
    return programs.none
  }

  if (isMiddleSchoolDashboard()) {
    return programs.middleSchoolWithTraining
  } else if (isK5Dashboard()) {
    return programs.elementaryWithTraining
  } else {
    // Default to none if not on a recognized dashboard
    return programs.none
  }
}

/**
 * Maps program status to CTA content from the provided CTA array
 *
 * @param {string} programStatus - The current program status from K8ProgramStatus enum
 * @param {Array} ctaArray - Array of CTA content objects from contentful
 * @returns {Object|null} The matching CTA content object or null if not found
 */
export const getCTAForProgramStatus = (programStatus, ctaArray = []) => {
  // Map of program status to expected use case
  const statusToUseCase = {
    MISSING_TEACHER_PREFERENCES: 'ftue',
    PREFERENCES_SET_TRAINING_REQUIRED_NO_CLASS: 'training_start',
    PREFERENCES_SET_TRAINING_NOT_REQUIRED_NO_CLASS: 'create_class',
    PREFERENCES_SET_SOME_TRAINING_NO_CLASS: 'training_resume',
    TRAINING_COMPLETE_NO_CLASS: 'create_class',
    TRAINING_REQUIRED_INCOMPLETE_CLASS_CREATED: 'training_resume',
    TRAINING_NOT_REQUIRED_INCOMPLETE_CLASS_CREATED: null, // No CTA needed for this state
    TRAINING_COMPLETE_CLASS_CREATED: null, // No CTA needed for this state
  }

  // Get the use case for the current status
  const useCase = statusToUseCase[programStatus]

  // If no use case is defined for this status or no CTA array provided, return null
  if (!useCase || !Array.isArray(ctaArray)) {
    return null
  }

  // Find the CTA content that matches the use case
  return ctaArray.find(item => item.useCase === useCase) || null
}
